import React, { useEffect, useRef, useState } from 'react';
import { Row, Col, Card, message, Button } from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import { ChallengeConfigProvider, defaultConfig, useChallengeConfig } from '../../contexts/BlocklyConfigContext';
import Blockly, { BlocklyRef } from '../Blockly';
import { TLesson } from '../../../../models/courses/lesson';
import { useCourseApi } from '../../../../api/courses/courses';
import LessonBlocklyPhaserTeacher from '../teacher/LessonBlocklyPhaserTeacher';

interface LessonBlocklyPhaserAdminProps {
  lesson: TLesson | undefined;
  teacherMode?: boolean;
}

const LessonBlocklyPhaserAdmin: React.FC<LessonBlocklyPhaserAdminProps> = ({ lesson, teacherMode }) => {
  return (
    <ChallengeConfigProvider>
      <LessonBlocklyPhaserAdminContent lesson={lesson} teacherMode={teacherMode} />
    </ChallengeConfigProvider>
  );
};

const LessonBlocklyPhaserAdminContent: React.FC<LessonBlocklyPhaserAdminProps> = ({ lesson, teacherMode }) => {
  const { config, setConfig } = useChallengeConfig();
  const blocklyRef = useRef<BlocklyRef>(null);
  const teacherBlocklyRef = useRef<BlocklyRef>(null);
  const { saveLessonDetails } = useCourseApi();
  const [importedStatement, setImportedStatement] = useState<string>('');
  const [latestTeacherWorkspace, setLatestTeacherWorkspace] = useState<string>('');

  useEffect(() => {
    const baseConfig = lesson?.config?.config ?? defaultConfig(true);
    setConfig({
      ...baseConfig,
      configWorkspace: {
        ...baseConfig.configWorkspace,
        adminMode: true,
      },
      configPhaser: {
        ...baseConfig.configPhaser,
        projectMode: true,
      },
    });
    // Initialize imported statement from lesson
    setImportedStatement(lesson?.statement || '');
    // Initialize teacherWorkspace from config
    setLatestTeacherWorkspace(baseConfig.configWorkspace?.teacherWorkspace || '');
  }, [lesson, setConfig]);

  const save = async (): Promise<void> => {
    if (!lesson) return;

    if (teacherMode) {
      // Teacher mode: only save teacherWorkspace, preserve workspace and configPhaser
      // Get current workspace XML from the teacher Blockly component
      if (!teacherBlocklyRef.current) {
        message.warning('Le composant Blockly n\'est pas encore initialisé. Veuillez patienter.');
        return;
      }
      
      const teacherWorkspaceXml = teacherBlocklyRef.current.getWorkspaceXml();
      
      if (!teacherWorkspaceXml || teacherWorkspaceXml.trim() === '') {
        message.warning('Aucun workspace à sauvegarder. Veuillez ajouter des blocs dans le workspace.');
        return;
      }
      
      const data: TLesson = {
        ...lesson,
        statement: importedStatement || lesson.statement || '',
        config: {
          id: lesson.config?.id || 0,
          lesson_id: lesson.id,
          config: {
            configWorkspace: {
              ...config.configWorkspace,
              teacherWorkspace: teacherWorkspaceXml,
            },
            configPhaser: config.configPhaser, // Preserve existing Phaser config
          },
        },
      };

      try {
        await saveLessonDetails(data);
        message.success('Leçon sauvegardée.');
      } catch (err: any) {
        message.error(err.response?.data ?? err.message ?? err);
      }
    } else {
      // Admin mode: save workspace and configPhaser
      if (!blocklyRef.current) return;
      const livePhaserConfig = blocklyRef.current.getPhaserConfig();
      const workspace = blocklyRef.current.getWorkspaceXml() ?? '';
      const data: TLesson = {
        ...lesson,
        // Prioritize imported statement over existing lesson statement
        statement: importedStatement || lesson.statement || '',
        config: {
          id: lesson.config?.id || 0,
          lesson_id: lesson.id,
          config: {
            configWorkspace: {
              ...config.configWorkspace,
              workspace,
              teacherWorkspace: latestTeacherWorkspace, // Preserve teacherWorkspace
            },
            configPhaser: livePhaserConfig ?? config.configPhaser,
          },
        },
      };

      try {
        await saveLessonDetails(data);
        message.success('Leçon sauvegardée.');
      } catch (err: any) {
        message.error(err.response?.data ?? err.message ?? err);
      }
    }
  };

  // Teacher mode: show teacher view (like student view)
  if (teacherMode) {
    return (
      <div style={{ width: '100%', height: '100%' }}>
        {config && (
          <LessonBlocklyPhaserTeacher
            lesson={lesson}
            blocklyRef={teacherBlocklyRef}
          />
        )}
        <Button 
          type="default" 
          shape="circle"
          icon={<SaveOutlined />} 
          onClick={save}
          style={{
            position: 'fixed',
            bottom: '24px',
            right: '24px',
            width: '56px',
            height: '56px',
            zIndex: 1000,
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            border: '1px solid rgba(0, 0, 0, 0.1)',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            color: '#1890ff'
          }}
        />
      </div>
    );
  }

  // Admin mode: show admin editing view
  return (
    <div style={{ width: '100%', height: '100%' }}>
      <Row gutter={16} style={{ padding: '0 16px 16px' }}>
        <Col span={24}>
          <Card style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Blockly 
              ref={blocklyRef} 
              onWin={() => { }} 
              adminMode={true}
              initialStatement={importedStatement}
              onStatementChange={setImportedStatement}
            />
          </Card>
        </Col>
      </Row>
      
      <Button 
        type="default" 
        shape="circle"
        icon={<SaveOutlined />} 
        onClick={save}
        style={{
          position: 'fixed',
          bottom: '24px',
          right: '24px',
          width: '56px',
          height: '56px',
          zIndex: 1000,
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          border: '1px solid rgba(0, 0, 0, 0.1)',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          color: '#1890ff'
        }}
      />
    </div>
  );
};

export default LessonBlocklyPhaserAdmin;
